import {
  format,
  getDate,
  getDay,
  isAfter,
  isValid,
  parse,
  setDate,
  setMonth,
  setYear,
  startOfMonth,
} from 'date-fns/esm'

const referenceDate = new Date()

export const parseDate = (date: string): Date => {
  try {
    return parse(date, 'yyyyMMdd', referenceDate)
  } catch (e) {
    // TODO: what to do here? Journal?
    throw new Error(`Cannot parse ${date}`)
  }
}

export const getDateAtMidnight = (date: Date = new Date()): Date => {
  const year: number = date.getFullYear()
  const month: number = date.getMonth()
  const day: number = date.getDate()
  return new Date(year, month, day)
}

export function convertToYYYYMMDD(date: Date): uui.domain.YYYYMMDD {
  return format(date, 'yyyyMMdd')
}

/**
 * Calculate the cardinality (occurrence number) of a weekday within its month
 * Example: July 17, 2025 (Thursday) -> 3 (it's the 3rd Thursday of July)
 */
export function getWeekdayCardinality(date: Date): number {
  const dayOfWeek = getDay(date) // 0 = Sunday, 1 = Monday, etc.
  const dayOfMonth = getDate(date) // 1-31
  const firstOfMonth = startOfMonth(date)
  const firstWeekday = getDay(firstOfMonth) // What weekday is the 1st?

  // Calculate how many days into the month the first occurrence of this weekday is
  const daysToFirstOccurrence = (dayOfWeek - firstWeekday + 7) % 7

  // Calculate which occurrence this is
  const cardinality = Math.floor((dayOfMonth - 1 - daysToFirstOccurrence) / 7) + 1

  return cardinality
}

/**
 * Calculate the Date starting from the weekday and the cardinality
 */
export function parseDateFromCardinality(
  cardinal: number,
  weekday: number,
  userMonth?: number,
  userYear?: number,
): Date {
  const month = userMonth ?? 0
  let date = new Date()
  date = setMonth(date, month - 1) // Set the month
  if (userYear !== undefined) {
    date = setYear(date, userYear) // Set the year if provided
  }
  date = setDate(date, 1) // Start from the first day of the month

  // Find the first occurrence of the specified weekday
  let firstOccurrence = 0
  // while (getWeekDayConsistentWithRecurrenceModel(date) !== weekday) {

  while (date.getDay() !== weekday) {
    date.setDate(date.getDate() + 1)
    firstOccurrence++
    if (firstOccurrence > 6) {
      throw new Error(
        `Cannot find the first occurrence of weekday ${weekday} in month ${month} after 7 days`,
      )
    }
  }

  // Calculate the date of the desired occurrence
  const desiredDate = 1 + firstOccurrence + (cardinal - 1) * 7
  date.setDate(desiredDate)

  if (!isValid(date)) {
    if (userMonth !== undefined) {
      throw new Error(
        `Invalid date generated from cardinal ${cardinal} and weekday ${weekday} and month ${userMonth}`,
      )
    }

    throw new Error(`Invalid date generated from cardinal ${cardinal} and weekday ${weekday}`)
  }

  return date
}

export function toClientWeekday(javaWeekday: number) {
  // BE DayOfWeek enum starts from 1 (Monday) to 7 (Sunday)
  // while client Date.getDay() starts from 0 (Sunday) to 6 (Saturday)
  return (javaWeekday === 7 ? 0 : javaWeekday) as uui.domain.client.rm.WeekDay
}

export function toServerWeekday(jsWeekday: number) {
  // BE DayOfWeek enum starts from 1 (Monday) to 7 (Sunday)
  // while client Date.getDay() starts from 0 (Sunday) to 6 (Saturday)
  return (jsWeekday === 0 ? 7 : jsWeekday) as uui.domain.WeekDay
}

export function isValidInterval({ start, end }: Interval): boolean {
  return isValid(start) && isValid(end) && isAfter(end, start)
}
